<template>
  <div class="account-forgot-password-form">
    <Heading
      :level="1"
      :title="t('checkout.forgotPassword.title')"
      class="mb-6"
    />
    <form class="form" @submit.prevent.stop="submitPasswordReset()">
      <div class="card-body">
        <div class="input">
          <label for="email" class="required form-label">
            {{ t('checkout.forgotPassword.form.email') }}
          </label>
          <input
            id="email"
            v-model="forgotPasswordForm.email"
            autocomplete="email"
            class="form-control"
            data-test-id="forgot-password-email"
            name="email"
            :class="{
              'is-invalid': invalidKeys.includes('email'),
            }"
            :disabled="isLoading"
            @input="clearErrors('email')"
            @keydown.enter.prevent="submitPasswordReset()"
          />
          <span v-if="invalidKeys.includes('email')" class="text-red text-xs">
            {{ errors.email }}
          </span>
        </div>

        <div class="d-flex flex-stack mt-4">
          <Button color="primary" class="w-full" :disabled="isLoading">
            {{ t('checkout.forgotPassword.form.submitButton') }}
          </Button>
        </div>

        <div class="forgot-pass-link mt-4">
          <button
            type="button"
            class="forgot-password-back-button"
            :disabled="isLoading"
            @click="handleBack"
          >
            {{ t('checkout.forgotPassword.backToLogin') }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { useFormValidation } from '~/composables/useFormValidation'

const { t } = useI18n()

defineProps<{
  isLoading: boolean
  email: string
}>()

const emit = defineEmits<{
  (e: 'submit', data: { email: string }): void
  (e: 'back'): void
  (e: 'email-change', email: string): void
}>()

const forgotPasswordForm = ref({
  email: '',
})

const rules = ref({
  email: 'required|email',
})

const { errors, clearErrors, validate, invalidKeys } = useFormValidation(
  forgotPasswordForm,
  rules
)

watch(
  () => forgotPasswordForm.value.email,
  (newEmail) => {
    emit('email-change', newEmail)
  }
)

const submitPasswordReset = async () => {
  await validate()
  if (invalidKeys.value.length > 0) {
    return
  }
  emit('submit', { email: forgotPasswordForm.value.email })
}

const handleBack = () => {
  emit('back')
}
</script>

<style lang="postcss" scoped>
.account-forgot-password-form {
  @apply max-w-lg w-full mx-auto;
}

.input {
  @apply flex flex-col gap-2 flex-1;

  input {
    @apply w-full rounded-lg px-2 h-[37px] border border-gray-200;
  }
}

.forgot-pass-link {
  @apply text-primary text-base;

  .forgot-password-back-button {
    @apply underline hover:no-underline border-0 p-0 cursor-pointer;
  }
}

.required:after {
  content: '*';
  position: relative;
  font-size: inherit;
  @apply text-red;
  padding-left: 0.25rem;
  font-weight: 700;
}
</style>
